import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';
import * as paperdollRenderer from '../../lib/paperdollRenderer';
import { VARIANTS as PD_VARIANTS, SLOT_LABELS as PD_LABELS, TINTABLE_SLOTS } from '../../lib/paperdolls';
import AbilityScoreCell from './AbilityScoreCell';
import WeaponProficiencyPicker from './WeaponProficiencyPicker';
import { parseImportedJson, summarize } from '../../lib/characterIO';

const DEFAULT_ABILITY_SCORES = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
// D&D 5e Standard Array — common starting allocation. Used by the "Standard
// Array" preset button to give new players a sensible quick-fill.
const STANDARD_ARRAY = { str: 15, dex: 14, con: 13, int: 12, wis: 10, cha: 8 };

export const DEFAULT_PAPERDOLL = {
  gender: 'M',
  widthScale:  1,
  heightScale: 1,
  tints: {},
  layers: {
    body:   'Skin_2',
    hair:   'Brown_3',
    top:    'Shirt_Buttoned',
    bottom: 'Pants_Leather_Thickened_Brown',
    feet:   'Leather_Simple',
    hands:  null,
    head:   null,
    beard:  null,
    cape:   null,
    chest:  null,
    overall: null,
    melee:  'Sword_Iron_Polished',
    ranged: null,
    magic:  null,
    offhand: null,
    quiver:  null,
    artifact: null,
  },
};

const ATTACK_ANIM = { melee: 'Mel', ranged: 'Ran', magic: 'Mag' };
const ANIM_FRAMES = { Idl: 4, Mov: 6, Mel: 6, Mag: 6, Ran: 6 };


const CLASSES = [
  'Barbarian', 'Bard', 'Cleric', 'Druid', 'Fighter',
  'Monk', 'Paladin', 'Ranger', 'Rogue', 'Sorcerer',
  'Warlock', 'Wizard', 'Artificer', 'Blood Hunter', 'Other',
];

const CLASS_ICONS = {
  Barbarian: '⚔️', Bard: '🎵', Cleric: '⛪', Druid: '🌿', Fighter: '🛡',
  Monk: '👊', Paladin: '✨', Ranger: '🏹', Rogue: '🗡', Sorcerer: '🔮',
  Warlock: '👁', Wizard: '🧙', Artificer: '⚙️', 'Blood Hunter': '🩸', Other: '⭐',
};

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}

export default function CharacterSetup({ onComplete }) {
  const { spawnToken, assignToken, updatePlayer, updateToken, tokens, players } = useCampaign();
  const { auth } = useAuth();

  const playerRecord = players[auth?.playerId];
  const isReturning  = !!(playerRecord?.charName); // has saved stats from a prior session

  const [step,      setStep]      = useState(1);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  // Form state — defaults first; overwritten by useEffect once playerRecord arrives
  const [charName,  setCharName]  = useState('');
  const [charClass, setCharClass] = useState('Fighter');
  const [level,     setLevel]     = useState(1);
  const [maxHp,     setMaxHp]     = useState(10);
  const [ac,        setAc]        = useState(10);
  const [initBonus, setInitBonus] = useState(0);
  const [speed,       setSpeed]       = useState(30);
  const [visionRange, setVisionRange] = useState(30);
  const [passivePerception,   setPassivePerception]   = useState(10);
  const [passiveInvestigation, setPassiveInvestigation] = useState(10);
  const [passiveInsight,      setPassiveInsight]      = useState(10);
  const [hue,        setHue]        = useState(43);
  const [sat,        setSat]        = useState(70);
  const [lit,        setLit]        = useState(55);
  const colour = hslToHex(hue, sat, lit);
  const [paperdoll,   setPaperdoll]   = useState(DEFAULT_PAPERDOLL);
  const [combatStyle, setCombatStyle] = useState('melee');
  const [sizeCategory, setSizeCategory] = useState('medium');
  const [abilityScores, setAbilityScores] = useState(DEFAULT_ABILITY_SCORES);
  const setAbility = (key, val) => setAbilityScores((s) => ({ ...s, [key]: val }));
  const [weaponProficiencies, setWeaponProficiencies] = useState([]);
  const [armorProficiencies,  setArmorProficiencies]  = useState([]);

  // Import flow — pick a JSON, validate, persist to player record, jump to step 3.
  const importFileRef = useRef(null);
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !auth?.playerId) return;
    const text = await file.text();
    const result = parseImportedJson(text);
    if (!result.ok) {
      setError(`Import failed: ${result.error}`);
      return;
    }
    if (!confirm(`Import ${summarize(result.data)}? This replaces your current character.`)) return;
    try {
      await updatePlayer(auth.playerId, result.data);
      setStep(3);
    } catch (err) {
      console.error(err);
      setError('Import failed during save.');
    }
  };

  // Pre-populate from saved record once it loads (only fires once)
  const prePopDone = useRef(false);
  useEffect(() => {
    if (!playerRecord || prePopDone.current) return;
    prePopDone.current = true;
    if (playerRecord.charName)           setCharName(playerRecord.charName);
    if (playerRecord.class)              setCharClass(playerRecord.class);
    if (playerRecord.level)              setLevel(playerRecord.level);
    if (playerRecord.maxHp)              setMaxHp(playerRecord.maxHp);
    if (playerRecord.ac)                 setAc(playerRecord.ac);
    if (playerRecord.initiativeBonus != null) setInitBonus(playerRecord.initiativeBonus);
    if (playerRecord.speed)              setSpeed(playerRecord.speed);
    if (playerRecord.visionRange != null) setVisionRange(playerRecord.visionRange);
    if (playerRecord.passivePerception   != null) setPassivePerception(playerRecord.passivePerception);
    if (playerRecord.passiveInvestigation != null) setPassiveInvestigation(playerRecord.passiveInvestigation);
    if (playerRecord.passiveInsight      != null) setPassiveInsight(playerRecord.passiveInsight);
    if (playerRecord.colour) {
      const [h, s, l] = hexToHsl(playerRecord.colour);
      setHue(h); setSat(s); setLit(l);
    }
    if (playerRecord.paperdoll) {
      const pd = { ...playerRecord.paperdoll };
      // Migrate legacy hairColor → tints.hair
      if (pd.hairColor && !pd.tints) pd.tints = { hair: pd.hairColor };
      if (!pd.tints) pd.tints = {};
      if (pd.widthScale  == null) pd.widthScale  = 1;
      if (pd.heightScale == null) pd.heightScale = 1;
      delete pd.hairColor;
      setPaperdoll(pd);
    }
    if (playerRecord.combatStyle)  setCombatStyle(playerRecord.combatStyle);
    if (playerRecord.sizeCategory) setSizeCategory(playerRecord.sizeCategory);
    if (playerRecord.abilityScores) {
      setAbilityScores({ ...DEFAULT_ABILITY_SCORES, ...playerRecord.abilityScores });
    }
    if (Array.isArray(playerRecord.armorProficiencies)) {
      setArmorProficiencies(playerRecord.armorProficiencies);
    }
    if (Array.isArray(playerRecord.weaponProficiencies)) {
      setWeaponProficiencies(playerRecord.weaponProficiencies);
    }
  }, [playerRecord]);

  const canProceed1 = charName.trim().length > 0;
  const canProceed2 = maxHp > 0;

  const handleCreate = async () => {
    if (!canProceed1 || !canProceed2) return;
    setSaving(true);
    setError('');
    try {
      // Persist character stats FIRST so the record (and its auto-snapshot) lands
      // durably even if the player closes the tab before token spawn completes.
      // PlayerView's auto-spawn effect will re-create the hidden token on next load.
      await updatePlayer(auth.playerId, {
        charName:        charName.trim(),
        class:           charClass,
        level,
        maxHp,
        ac,
        initiativeBonus: initBonus,
        speed,
        visionRange,
        passivePerception,
        passiveInvestigation,
        passiveInsight,
        colour,
        paperdoll,
        combatStyle,
        sizeCategory,
        abilityScores,
        weaponProficiencies,
        armorProficiencies,
      });
      // If this player already has a token (level-up / re-edit flow), update
      // it in place instead of creating a duplicate with old stats lingering
      // on the original. Without this the DM saw a stale token while the
      // player record had the new values.
      const existingToken = Object.values(tokens || {}).find(
        (t) => t.type === 'player' && t.ownerId === auth.playerId
      );

      const tokenFields = {
        name:            charName.trim(),
        colour,
        // Preserve current hp delta when leveling up: bump maxHp but keep
        // current hp at most at the new maxHp.
        maxHp,
        ac,
        initiativeBonus: initBonus,
        speed,
        visionRange,
        passivePerception,
        passiveInvestigation,
        passiveInsight,
        class:           charClass,
        level,
        paperdoll,
        combatStyle,
        sizeCategory,
      };

      if (existingToken) {
        const nextHp = Math.min(existingToken.hp ?? maxHp, maxHp);
        await updateToken(existingToken.id, { ...tokenFields, hp: nextHp });
      } else {
        // First-time creation — spawn hidden; DM reveals/places.
        const tokenId = await spawnToken({
          ...tokenFields,
          type:    'player',
          hp:      maxHp,
          hidden:  true,
          ownerId: auth.playerId,
        });
        await assignToken(tokenId, auth.playerId);
      }
      onComplete?.();
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.screen}>
      <div style={{ ...styles.card, maxWidth: step === 1 ? 600 : 400 }}>
        {/* Progress dots */}
        <div style={styles.progress}>
          {[1,2,3].map((s) => (
            <div key={s} style={{ ...styles.dot, ...(step >= s ? styles.dotActive : {}) }} />
          ))}
        </div>

        {/* ── Step 1: Appearance ───────────────────────────────── */}
        {step === 1 && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>{isReturning ? 'Update Your Character' : 'Create Your Character'}</h2>
              <p style={styles.subtitle}>Name your character and dress them up.</p>
            </div>

            {/* Hidden file input + Import button — for restoring from a saved JSON */}
            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button
              type="button"
              className="btn-ghost"
              style={{ width: '100%', marginBottom: 12 }}
              onClick={() => importFileRef.current?.click()}
            >
              📂 Import character from file
            </button>

            <div style={{ ...styles.field, marginBottom: 16 }}>
              <label style={styles.label}>Character Name</label>
              <input
                placeholder="e.g. Aria Swiftblade"
                value={charName}
                onChange={(e) => setCharName(e.target.value)}
                autoFocus
              />
            </div>

            <PaperdollComposer
              value={paperdoll}
              onChange={setPaperdoll}
              combatStyle={combatStyle}
              onCombatStyleChange={setCombatStyle}
            />

            <button
              className="btn-primary w-full"
              style={styles.nextBtn}
              onClick={() => setStep(2)}
              disabled={!canProceed1}
            >
              Next →
            </button>
          </>
        )}

        {/* ── Step 2: Stats ────────────────────────────────────── */}
        {step === 2 && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>{charName || 'Your Character'}</h2>
              <p style={styles.subtitle}>Stats & class</p>
            </div>

            <div style={styles.fields}>
              <div style={styles.field}>
                <label style={styles.label}>Class</label>
                <div style={styles.classGrid}>
                  {CLASSES.map((c) => (
                    <button
                      key={c}
                      style={{ ...styles.classBtn, ...(charClass === c ? styles.classBtnActive : {}) }}
                      onClick={() => setCharClass(c)}
                    >
                      <span style={styles.classIcon}>{CLASS_ICONS[c]}</span>
                      <span style={styles.className}>{c}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Level</label>
                <div style={styles.levelRow}>
                  {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map((l) => (
                    <button
                      key={l}
                      style={{ ...styles.levelBtn, ...(level === l ? styles.levelBtnActive : {}) }}
                      onClick={() => setLevel(l)}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Token Colour</label>
                <div style={styles.colourPreviewRow}>
                  <div style={{ ...styles.colourSwatch, background: colour }} />
                  <div style={styles.colourSliders}>
                    <div style={styles.sliderRow}>
                      <span style={styles.sliderLabel}>Hue</span>
                      <input type="range" min={0} max={360} value={hue} onChange={(e) => setHue(+e.target.value)}
                        style={{ ...styles.slider, background: `linear-gradient(to right, hsl(0,${sat}%,${lit}%), hsl(60,${sat}%,${lit}%), hsl(120,${sat}%,${lit}%), hsl(180,${sat}%,${lit}%), hsl(240,${sat}%,${lit}%), hsl(300,${sat}%,${lit}%), hsl(360,${sat}%,${lit}%))` }} />
                    </div>
                    <div style={styles.sliderRow}>
                      <span style={styles.sliderLabel}>Saturation</span>
                      <input type="range" min={0} max={100} value={sat} onChange={(e) => setSat(+e.target.value)}
                        style={{ ...styles.slider, background: `linear-gradient(to right, hsl(${hue},0%,${lit}%), hsl(${hue},100%,${lit}%))` }} />
                    </div>
                    <div style={styles.sliderRow}>
                      <span style={styles.sliderLabel}>Brightness</span>
                      <input type="range" min={15} max={85} value={lit} onChange={(e) => setLit(+e.target.value)}
                        style={{ ...styles.slider, background: `linear-gradient(to right, hsl(${hue},${sat}%,15%), hsl(${hue},${sat}%,50%), hsl(${hue},${sat}%,85%))` }} />
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.abilityHeader}>
                  <label style={styles.label}>Ability Scores</label>
                  <button
                    type="button"
                    style={styles.presetBtn}
                    onClick={() => setAbilityScores(STANDARD_ARRAY)}
                    title="Fill with the 5e Standard Array (15, 14, 13, 12, 10, 8)"
                  >
                    Standard Array
                  </button>
                </div>
                <div style={styles.abilityGrid}>
                  <AbilityScoreCell label="STR" value={abilityScores.str} onChange={(v) => setAbility('str', v)} />
                  <AbilityScoreCell label="DEX" value={abilityScores.dex} onChange={(v) => setAbility('dex', v)} />
                  <AbilityScoreCell label="CON" value={abilityScores.con} onChange={(v) => setAbility('con', v)} />
                  <AbilityScoreCell label="INT" value={abilityScores.int} onChange={(v) => setAbility('int', v)} />
                  <AbilityScoreCell label="WIS" value={abilityScores.wis} onChange={(v) => setAbility('wis', v)} />
                  <AbilityScoreCell label="CHA" value={abilityScores.cha} onChange={(v) => setAbility('cha', v)} />
                </div>
                <div style={styles.abilityHint}>
                  These drive minigame matchups (DEX → Whack-a-Mole, INT → High Card, etc.) and your modifiers.
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Weapon Proficiencies</label>
                <WeaponProficiencyPicker
                  value={weaponProficiencies}
                  onChange={setWeaponProficiencies}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Armor Proficiencies</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { id: 'light',  label: '🛡 Light' },
                    { id: 'medium', label: '🛡 Medium' },
                    { id: 'heavy',  label: '🛡 Heavy' },
                    { id: 'shield', label: '🛡 Shields' },
                  ].map((opt) => {
                    const active = (armorProficiencies || []).includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          const list = armorProficiencies || [];
                          setArmorProficiencies(
                            active ? list.filter((x) => x !== opt.id) : [...list, opt.id],
                          );
                        }}
                        style={{
                          padding: '6px 10px', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace",
                          background: active ? 'rgba(212,168,71,0.18)' : '#0e1528',
                          border: `1px solid ${active ? '#d4a847' : '#4a3d28'}`,
                          color: active ? '#d4a847' : '#9a8b6e',
                          borderRadius: 4, cursor: 'pointer',
                        }}
                      >{opt.label}</button>
                    );
                  })}
                </div>
                <div style={{ color: '#8a7e68', fontSize: 11, marginTop: 4 }}>
                  Without proficiency, wearing armor or a shield gives disadvantage on STR/DEX checks, saves, and attack rolls — and you can't cast spells. AC is unchanged.
                </div>
              </div>

              <div style={styles.statGrid}>
                <StatInput label="Max HP" value={maxHp} onChange={setMaxHp} min={1} hint="Your full HP at this level" />
                <StatInput label="Armour Class" value={ac} onChange={setAc} min={1} max={30} hint="Your total AC with armour & shield" />
                <StatInput label="Initiative Bonus" value={initBonus} onChange={setInitBonus} min={-5} max={20} hint="Usually your DEX modifier" signed />
                <StatInput label="Speed (ft)" value={speed} onChange={setSpeed} min={0} max={120} hint="Walking speed in feet" />
                <StatInput label="Vision Range (ft)" value={visionRange} onChange={setVisionRange} min={0} max={120} hint="30ft normal · 60ft darkvision · 0 = no auto-reveal" />
                <StatInput label="Passive Perception"   value={passivePerception}   onChange={setPassivePerception}   min={0} max={30} hint="10 + Perception skill mod" />
                <StatInput label="Passive Investigation" value={passiveInvestigation} onChange={setPassiveInvestigation} min={0} max={30} hint="10 + Investigation skill mod" />
                <StatInput label="Passive Insight"       value={passiveInsight}      onChange={setPassiveInsight}      min={0} max={30} hint="10 + Insight skill mod" />
              </div>

              <div style={{ ...styles.field, marginTop: 12 }}>
                <label style={styles.label}>Size Category</label>
                <select
                  value={sizeCategory}
                  onChange={(e) => setSizeCategory(e.target.value)}
                  style={{ padding: '6px 8px', fontSize: 13, fontWeight: 600, background: '#141c30', color: '#d0d0e0', border: '1px solid #4a3d28', borderRadius: 4 }}
                >
                  <option value="tiny">Tiny</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="huge">Huge</option>
                  <option value="gargantuan">Gargantuan</option>
                </select>
                <div style={{ color: '#8a7e68', fontSize: 11, marginTop: 4 }}>Halflings / gnomes are Small. Most races are Medium. Changes pass-through &amp; difficult-terrain rules.</div>
              </div>
            </div>

            <div style={styles.btnRow}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>← Back</button>
              <button
                className="btn-primary"
                style={{ flex: 2 }}
                onClick={() => setStep(3)}
                disabled={!canProceed2}
              >
                Review →
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Confirm ──────────────────────────────────── */}
        {step === 3 && (
          <>
            <div style={styles.header}>
              <div style={{ ...styles.tokenPreview, background: colour }}>
                <PaperdollTokenPreview paperdoll={paperdoll} />
              </div>
              <h2 style={styles.title}>{charName}</h2>
              <p style={styles.subtitle}>{charClass} · Level {level}</p>
            </div>

            <div style={styles.summaryGrid}>
              <SummaryRow label="Max HP"           value={maxHp} />
              <SummaryRow label="Armour Class"     value={ac} />
              <SummaryRow label="Initiative Bonus" value={initBonus >= 0 ? `+${initBonus}` : initBonus} />
              <SummaryRow label="Speed"            value={`${speed} ft`} />
              <SummaryRow label="Vision Range"    value={visionRange > 0 ? `${visionRange} ft` : 'None'} />
              <SummaryRow label="Token Colour"     value={<span style={{ color: colour }}>■ {colour}</span>} />
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.btnRow}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setStep(2)}>← Edit</button>
              <button
                className="btn-primary"
                style={{ flex: 2, fontSize: 16 }}
                onClick={handleCreate}
                disabled={saving}
              >
                {saving ? 'Creating…' : '⚔ Ready to Play'}
              </button>
            </div>

            <p style={styles.note}>Your DM will place you on the map. Your stats are saved for next session.</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatInput({ label, value, onChange, min = 0, max = 999, hint, signed }) {
  return (
    <div style={styles.statBox}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statControls}>
        <button style={styles.statBtn} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
        <span style={styles.statValue}>{signed && value > 0 ? `+${value}` : value}</span>
        <button style={styles.statBtn} onClick={() => onChange(Math.min(max, value + 1))}>+</button>
      </div>
      {hint && <div style={styles.statHint}>{hint}</div>}
    </div>
  );
}

const PREVIEW_BGS = [
  { id: 'dark',  color: '#0a0f1e' },
  { id: 'mid',   color: '#3a3325' },
  { id: 'light', color: '#c9bfa3' },
  { id: 'grass', color: '#2d4a2b' },
  { id: 'stone', color: '#5a5d66' },
];
const PREVIEW_SIZE = 288;

// Tiles render this direction so the currently-edited slot is actually visible
// (capes and quivers are behind the body in the down-facing view).
const TILE_DIR = {
  cape: 'up',
  quiver: 'up',
  ranged: 'up',
  magic: 'right',
  melee: 'right',
  offhand: 'left',
};

export function PaperdollComposer({ value, onChange, combatStyle, onCombatStyleChange }) {
  const [tab, setTab]         = useState('body');
  const [dir, setDir]         = useState('down');
  const [animMode, setAnimMode] = useState('Idl'); // 'Idl' | 'Mov' | attack
  const [frame, setFrame]     = useState(0);
  const [bgIdx, setBgIdx]     = useState(0);
  const [, bumpVersion]       = useState(0);
  const previewRef            = useRef(null);

  const frameCount = ANIM_FRAMES[animMode] || 4;

  // Animation loop for the preview
  useEffect(() => {
    setFrame(0);
    const id = setInterval(() => setFrame((f) => (f + 1) % frameCount), 150);
    return () => clearInterval(id);
  }, [frameCount]);
  // Re-render when atlas images finish loading
  useEffect(() => paperdollRenderer.subscribe(() => bumpVersion((v) => v + 1)), []);
  // Paint preview
  useEffect(() => {
    if (!previewRef.current) return;
    const canvas = paperdollRenderer.getPaperdollFrame(value, animMode, dir, frame, PREVIEW_SIZE);
    const ctx = previewRef.current.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = PREVIEW_BGS[bgIdx].color;
    ctx.fillRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    if (canvas) ctx.drawImage(canvas, 0, 0);
  });

  const setSlot = (slot, variant) => {
    const next = { ...value.layers, [slot]: variant };
    // Weapon slots are mutually exclusive — picking one clears the other two
    // and updates combatStyle so the Attack preview uses the right animation.
    if (variant != null && (slot === 'melee' || slot === 'ranged' || slot === 'magic')) {
      for (const k of ['melee','ranged','magic']) if (k !== slot) next[k] = null;
      onCombatStyleChange(slot);
      setAnimMode(ATTACK_ANIM[slot]);
    }
    onChange({ ...value, layers: next });
  };

  const tabs = useMemo(() => [
    'body','hair','beard','top','overall','bottom','feet','hands','cape','head','chest',
    'melee','ranged','magic','offhand','quiver','artifact',
  ], []);

  useEffect(() => { if (!tabs.includes(tab)) setTab('body'); }, [tabs, tab]);

  // Auto-rotate the preview so the edited slot is visible
  useEffect(() => { setDir(TILE_DIR[tab] || 'down'); }, [tab]);

  const variants = PD_VARIANTS[tab] || [];
  const current  = value.layers[tab] ?? null;

  const animPills = [
    ['Idl', 'Idle'],
    ['Mov', 'Walk'],
    [ATTACK_ANIM[combatStyle], 'Attack'],
  ];

  const widthPct  = Math.round((value.widthScale  ?? 1) * 100);
  const heightPct = Math.round((value.heightScale ?? 1) * 100);
  const setScale  = (k, pct) => onChange({ ...value, [k]: pct / 100 });

  const tints = value.tints || {};
  const setTint = (slot, color) => {
    const next = { ...tints };
    if (color == null) delete next[slot];
    else next[slot] = color;
    onChange({ ...value, tints: next });
  };

  return (
    <div style={pdStyles.wrap}>
      <div style={pdStyles.sticky}>
        <canvas
          ref={previewRef}
          width={PREVIEW_SIZE}
          height={PREVIEW_SIZE}
          style={pdStyles.previewCanvas}
        />
        <div style={pdStyles.pillRow}>
          {animPills.map(([a, label]) => (
            <button
              key={a}
              style={{ ...pdStyles.pill, ...(animMode === a ? pdStyles.pillActive : {}) }}
              onClick={() => setAnimMode(a)}
            >{label}</button>
          ))}
        </div>
        <div style={pdStyles.pillRow}>
          {[['up','↑'],['left','←'],['down','↓'],['right','→']].map(([d, icon]) => (
            <button
              key={d}
              style={{ ...pdStyles.pill, ...(dir === d ? pdStyles.pillActive : {}) }}
              onClick={() => setDir(d)}
            >{icon}</button>
          ))}
        </div>
        <div style={pdStyles.bgRow}>
          {PREVIEW_BGS.map((bg, i) => (
            <button
              key={bg.id}
              onClick={() => setBgIdx(i)}
              title={bg.id}
              style={{
                ...pdStyles.bgSwatch,
                background: bg.color,
                borderColor: bgIdx === i ? '#d4a847' : '#4a3d28',
              }}
            />
          ))}
        </div>
        <div style={pdStyles.scaleRow}>
          <label style={pdStyles.scaleLabel}>Width <span style={pdStyles.scaleVal}>{widthPct}%</span></label>
          <input type="range" min={50} max={115} value={widthPct}
            onChange={(e) => setScale('widthScale', +e.target.value)}
            style={styles.slider} />
        </div>
        <div style={pdStyles.scaleRow}>
          <label style={pdStyles.scaleLabel}>Height <span style={pdStyles.scaleVal}>{heightPct}%</span></label>
          <input type="range" min={85} max={115} value={heightPct}
            onChange={(e) => setScale('heightScale', +e.target.value)}
            style={styles.slider} />
        </div>
      </div>

      <div style={pdStyles.tabRow}>
        {tabs.map((t) => (
          <button
            key={t}
            style={{ ...pdStyles.tab, ...(tab === t ? pdStyles.tabActive : {}) }}
            onClick={() => setTab(t)}
          >{PD_LABELS[t] || t}</button>
        ))}
      </div>

      <div style={pdStyles.grid}>
        {variants.map((v) => (
          <VariantTile
            key={String(v)}
            gender={value.gender}
            body={value.layers.body}
            tint={tints[tab] || null}
            slot={tab}
            variant={v}
            dir={TILE_DIR[tab] || 'down'}
            selected={current === v}
            onClick={() => setSlot(tab, v)}
          />
        ))}
      </div>

      {TINTABLE_SLOTS.has(tab) && (
        <div style={pdStyles.tintBlock}>
          <div style={styles.label}>{PD_LABELS[tab] || tab} Colour</div>
          <SlotColourPicker
            value={tints[tab] ?? null}
            onChange={(c) => setTint(tab, c)}
          />
        </div>
      )}
    </div>
  );
}

function SlotColourPicker({ value, onChange }) {
  // Parse current hex → HSL; default to mid-saturation brown-ish when null.
  const [hue, setHue] = useState(() => value ? hexToHsl(value)[0] : 30);
  const [sat, setSat] = useState(() => value ? hexToHsl(value)[1] : 55);
  const [lit, setLit] = useState(() => value ? hexToHsl(value)[2] : 45);
  const isNatural = value == null;

  const push = (h, s, l) => onChange(hslToHex(h, s, l));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={pdStyles.pillRow}>
        <button
          style={{ ...pdStyles.pill, ...(isNatural ? pdStyles.pillActive : {}) }}
          onClick={() => onChange(null)}
        >Natural</button>
        <button
          style={{ ...pdStyles.pill, ...(!isNatural ? pdStyles.pillActive : {}) }}
          onClick={() => push(hue, sat, lit)}
        >Custom</button>
      </div>
      <input
        type="range" min={0} max={360} value={hue}
        onChange={(e) => { const h = +e.target.value; setHue(h); push(h, sat, lit); }}
        style={{ ...styles.slider, background: 'linear-gradient(to right, hsl(0,80%,50%), hsl(60,80%,50%), hsl(120,80%,50%), hsl(180,80%,50%), hsl(240,80%,50%), hsl(300,80%,50%), hsl(360,80%,50%))' }}
      />
      <input
        type="range" min={0} max={100} value={sat}
        onChange={(e) => { const s = +e.target.value; setSat(s); push(hue, s, lit); }}
        style={{ ...styles.slider, background: `linear-gradient(to right, hsl(${hue},0%,${lit}%), hsl(${hue},100%,${lit}%))` }}
      />
      <input
        type="range" min={10} max={85} value={lit}
        onChange={(e) => { const l = +e.target.value; setLit(l); push(hue, sat, l); }}
        style={{ ...styles.slider, background: `linear-gradient(to right, hsl(${hue},${sat}%,10%), hsl(${hue},${sat}%,50%), hsl(${hue},${sat}%,85%))` }}
      />
    </div>
  );
}

function VariantTile({ gender, body, tint = null, slot, variant, dir = 'down', selected, onClick }) {
  const ref     = useRef(null);
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [, bump] = useState(0);

  // Observe visibility so only on-screen tiles paint (matters for Head: 68 variants)
  useEffect(() => {
    if (!rootRef.current || visible) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: '120px' });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => paperdollRenderer.subscribe(() => bump((v) => v + 1)), []);

  useEffect(() => {
    if (!visible || !ref.current) return;
    const layers = slot === 'body' ? { body: variant } : { body, [slot]: variant };
    const tints = tint ? { [slot]: tint } : {};
    const canvas = paperdollRenderer.getPaperdollFrame({ gender, tints, layers }, 'Idl', dir, 0, 128);
    const ctx = ref.current.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 128, 128);
    if (canvas) ctx.drawImage(canvas, 0, 0);
  });

  return (
    <button
      ref={rootRef}
      onClick={onClick}
      style={{
        ...pdStyles.tile,
        ...(selected ? pdStyles.tileActive : {}),
      }}
    >
      {variant === null ? (
        <span style={pdStyles.tileNone}>✕</span>
      ) : (
        <canvas
          ref={ref}
          width={128}
          height={128}
          style={{ imageRendering: 'pixelated', width: '100%', height: '100%', display: 'block' }}
        />
      )}
    </button>
  );
}

function PaperdollTokenPreview({ paperdoll, size = 72, zoom = 2.5 }) {
  const ref = useRef(null);
  const [, bump] = useState(0);
  const [frame, setFrame] = useState(0);
  useEffect(() => paperdollRenderer.subscribe(() => bump((v) => v + 1)), []);
  useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % 4), 180);
    return () => clearInterval(id);
  }, []);
  const renderPx = size * 2;
  const displayPx = size * zoom;
  useEffect(() => {
    if (!ref.current) return;
    const canvas = paperdollRenderer.getPaperdollFrame(paperdoll, 'Idl', 'down', frame, renderPx);
    const ctx = ref.current.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, renderPx, renderPx);
    if (canvas) ctx.drawImage(canvas, 0, 0);
  });
  // Character feet sit at y≈85/128 of the frame. Position absolutely so the
  // zoomed-in canvas overflows the circle, centred on torso with feet near
  // the circle's bottom. The parent circle has overflow:hidden, clipping.
  const FEET_RATIO = 85 / 128;
  const feetY = displayPx * FEET_RATIO;
  const targetFeetY = size * 0.9;
  return (
    <canvas
      ref={ref}
      width={renderPx}
      height={renderPx}
      style={{
        imageRendering: 'pixelated',
        width: displayPx,
        height: displayPx,
        position: 'absolute',
        left: (size - displayPx) / 2,
        top: targetFeetY - feetY,
        pointerEvents: 'none',
      }}
    />
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={styles.summaryRow}>
      <span style={styles.summaryLabel}>{label}</span>
      <span style={styles.summaryValue}>{value}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  screen:          { width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'radial-gradient(ellipse at 50% 0%, #162844 0%, #0a0f1e 70%)', padding: 16, overflowY: 'auto', overflowX: 'hidden' },
  card:            { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, padding: '28px 24px', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
  progress:        { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 },
  dot:             { width: 8, height: 8, borderRadius: '50%', background: '#4a3d28' },
  dotActive:       { background: '#d4a847' },
  header:          { textAlign: 'center', marginBottom: 24 },
  icon:            { fontSize: 48, marginBottom: 10 },
  title:           { fontSize: 22, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#f0e6d0', marginBottom: 4 },
  subtitle:        { color: '#9a8b6e', fontSize: 14 },
  fields:          { display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 20 },
  field:           { display: 'flex', flexDirection: 'column', gap: 8 },
  label:           { fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9a8b6e' },

  classGrid:       { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  classBtn:        { padding: '8px 4px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, transition: 'border-color 0.12s' },
  classBtnActive:  { borderColor: '#d4a847', background: '#1a2a48' },
  classIcon:       { fontSize: 18 },
  className:       { fontSize: 9, color: '#9a8b6e', fontWeight: 600, textAlign: 'center' },

  levelRow:        { display: 'flex', flexWrap: 'wrap', gap: 4 },
  levelBtn:        { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  levelBtnActive:  { background: '#1a2a48', borderColor: '#d4a847', color: '#d4a847' },

  colourPreviewRow: { display: 'flex', alignItems: 'center', gap: 16 },
  colourSwatch:    { width: 56, height: 56, borderRadius: '50%', border: '2px solid #4a3d28', flexShrink: 0, boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.3)' },
  colourSliders:   { display: 'flex', flexDirection: 'column', gap: 10, flex: 1 },
  sliderRow:       { display: 'flex', alignItems: 'center', gap: 8 },
  sliderLabel:     { fontSize: 10, color: '#9a8b6e', width: 62, flexShrink: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  slider:          { flex: 1, height: 8, WebkitAppearance: 'none', appearance: 'none', borderRadius: 4, outline: 'none', cursor: 'pointer' },

  statGrid:        { display: 'flex', flexDirection: 'column', gap: 14 },
  abilityHeader:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  abilityGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 6 },
  abilityHint:     { fontSize: 11, color: '#6a5d45', lineHeight: 1.4, marginTop: 6 },
  presetBtn:       { background: 'rgba(212,168,71,0.12)', border: '1px solid #4a3d28', color: '#d4a847', padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
  statBox:         { background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, padding: '14px 16px' },
  statLabel:       { fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9a8b6e', marginBottom: 10 },
  statControls:    { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 },
  statBtn:         { width: 40, height: 40, background: '#142038', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statValue:       { fontSize: 32, fontWeight: 800, color: '#f0e6d0', minWidth: 60, textAlign: 'center' },
  statHint:        { fontSize: 11, color: '#6a5d45', textAlign: 'center', marginTop: 8 },

  tokenPreview:    { width: 72, height: 72, borderRadius: '50%', position: 'relative', margin: '0 auto 12px', boxShadow: '0 0 24px rgba(212,168,71,0.3)', overflow: 'hidden' },
  tokenInitial:    { fontSize: 32, fontWeight: 800, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.5)' },

  summaryGrid:     { display: 'flex', flexDirection: 'column', gap: 0, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, overflow: 'hidden', marginBottom: 20 },
  summaryRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #1a2a48' },
  summaryLabel:    { fontSize: 13, color: '#9a8b6e' },
  summaryValue:    { fontSize: 14, fontWeight: 700, color: '#f0e6d0' },

  btnRow:          { display: 'flex', gap: 8, marginBottom: 8 },
  nextBtn:         { padding: '14px 0', fontSize: 16 },
  error:           { color: '#c0392b', fontSize: 13, background: 'rgba(192,57,43,0.1)', padding: '8px 12px', borderRadius: 4, marginBottom: 12 },
  note:            { fontSize: 11, color: '#6a5d45', textAlign: 'center', marginTop: 12, lineHeight: 1.5 },
};

const pdStyles = {
  wrap:            { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 },
  sticky:          { position: 'sticky', top: -28, zIndex: 5, background: '#142038', paddingBottom: 8, paddingTop: 8, marginTop: -8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, borderBottom: '1px solid #2a2518' },
  previewRow:      { display: 'flex', gap: 12, alignItems: 'flex-start' },
  previewCanvas:   { width: 288, height: 288, imageRendering: 'pixelated', border: '2px solid #4a3d28', borderRadius: 4, flexShrink: 0 },
  scaleRow:        { display: 'flex', alignItems: 'center', gap: 8, width: '100%', maxWidth: 288 },
  scaleLabel:      { fontSize: 10, fontWeight: 700, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.07em', flex: '0 0 70px', display: 'flex', justifyContent: 'space-between' },
  scaleVal:        { color: '#d4a847' },
  tintBlock:       { display: 'flex', flexDirection: 'column', gap: 6, padding: 10, background: '#0a0f1e', border: '1px solid #4a3d28', borderRadius: 4 },
  bgRow:           { display: 'flex', gap: 4 },
  bgSwatch:        { width: 24, height: 24, borderRadius: 4, border: '2px solid #4a3d28', cursor: 'pointer', padding: 0 },
  rightCol:        { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  pillRow:         { display: 'flex', gap: 4, flexWrap: 'wrap' },
  pillCol:         { display: 'flex', flexDirection: 'column', gap: 4 },
  pill:            { flex: 1, minWidth: 0, padding: '8px 6px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  pillActive:      { background: '#1a2a48', borderColor: '#d4a847', color: '#d4a847' },
  tabRow:          { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, paddingBottom: 4 },
  tab:             { padding: '6px 4px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' },
  tabActive:       { background: '#1a2a48', borderColor: '#d4a847', color: '#d4a847' },
  grid:            { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: '1fr', gap: 8, padding: 6, background: '#0a0f1e', border: '1px solid #4a3d28', borderRadius: 4 },
  tile:            { width: '100%', aspectRatio: '1', minHeight: 120, padding: 0, background: '#0e1528', border: '2px solid #2a2518', borderRadius: 3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  tileActive:      { borderColor: '#d4a847', background: '#1a2a48' },
  tileNone:        { fontSize: 20, color: '#6a5d45' },
};

